// --- React & Redux
import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// --- Actions & Hooks
import { fetchUserPermissions } from "../tap/store/auth";
import { setAuthError } from "../tap/store/auth/actions";
// --- External Libraries
import queryString from "query-string";
// --- Components
import { SecuredApp } from "./SecuredApp/SecuredApp";
import TAPRouter from "../tap/router";

/**
 * Validates a redirect path to prevent open redirect attacks.
 * Only allows relative paths that start with a single forward slash.
 * @param {string} path - The path to validate
 * @returns {string} The validated path or "/home" if invalid
 */
function getSafeRedirectPath(path) {
  if (!path || typeof path !== "string") {
    return "/home";
  }

  // Must start with exactly one forward slash (relative path)
  // Reject: "//evil.com", "http://", "https://", "javascript:", etc.
  const isRelativePath = /^\/[^/]/.test(path) || path === "/";
  const hasProtocol = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path);

  if (!isRelativePath || hasProtocol) {
    return "/home";
  }

  return path;
}

export function SecureLogin({ LoginComp }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auth } = useSelector((s) => s);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    async function setTokenAsync() {
      await setToken();
    }

    const values = queryString.parse(location.search);
    if (values.postLoginRedirect && !auth?.token) {
      localStorage.setItem("postLoginRedirect", values.postLoginRedirect);
    }

    setTokenAsync();
    // eslint-disable-next-line
  }, [location.search]);

  useEffect(() => {
    if (isAuthenticating) {
      return;
    }
    checkCredentials();
    // eslint-disable-next-line
  }, [auth?.token, isAuthenticating]);

  async function setToken() {
    const values = queryString.parse(location.search);

    if (values.token) {
      setIsAuthenticating(true);
      try {
        await dispatch(fetchUserPermissions(values.token));
      } finally {
        setIsAuthenticating(false);
      }
    } else if (values.error) {
      // Decode URL-encoded error message and pass as plain text
      const errorMessage = decodeURIComponent(values.error);
      await dispatch(setAuthError(errorMessage));
    }
  }

  function checkCredentials() {
    const withCredentials = auth?.token;
    if (withCredentials) {
      const storedPath = localStorage.getItem("postLoginRedirect");
      if (!storedPath) {
        localStorage.removeItem("postLoginRedirect");
        navigate("/home", { replace: true });
        return;
      }
      if (
        window.location.href === storedPath ||
        window.location.pathname + window.location.search === storedPath
      ) {
        localStorage.removeItem("postLoginRedirect");
        return;
      }
      localStorage.removeItem("postLoginRedirect");
      if (/^https?:\/\//i.test(storedPath)) {
        window.location.replace(storedPath);
        return;
      }
      const redirectPath = getSafeRedirectPath(storedPath);
      navigate(redirectPath, { replace: true });
      const [pathname, search] = redirectPath.split("?");
      localStorage.setItem("activePanel", pathname);
      localStorage.setItem("activePanelSearch", search ? `?${search}` : "");
    } else {
      const values = queryString.parse(location.search);
      if (!values.token) {
        navigate("/login", { replace: true });
      }
    }
  }

  return <LoginComp loginFailed />;
}

export function AuthProvider({ AppComp, LoginComp, ...props }) {
  return (
    <Routes>
      <Route
        path="/login"
        element={<SecureLogin LoginComp={LoginComp} {...props} />}
      />
      <Route
        path="/"
        element={<SecureLogin LoginComp={LoginComp} {...props} />}
      />
      <Route path="/*" element={<SecuredApp App={TAPRouter} />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
